// Copyright (c) 2026 SpicyFriends
import { HotSpotIndicator, type HotSpotIndicatorProps } from '@repo/shared-client/components/blocks'
import { cn } from '@repo/shared-client/lib/utils'
import { isHelpDismissed, useSetHelpDismissed } from '@web/src/features/help/help-utils'
import { useAuth } from '@web/src/hooks/use-auth'
import type { ComponentProps } from 'react'
import { useEffect, useState } from 'react'

interface DismissHelpHotSpotProps {
  children?: React.ReactNode
  helpType: string
  showHotSpotIndicator?: boolean
  hotSpotColor?: string
  hotSpotFloatProps?: HotSpotIndicatorProps['floatProps']
  wrapperProps?: ComponentProps<'div'>
}

const DismissHelpHotSpot = ({
  children,
  helpType,
  showHotSpotIndicator = true,
  hotSpotColor,
  hotSpotFloatProps,
  wrapperProps,
}: DismissHelpHotSpotProps) => {
  const { currentUser, isAuthenticated, refreshSession } = useAuth()
  const { dismissHelp } = useSetHelpDismissed()
  const [isHotSpotDismissed, setIsHotSpotDismissed] = useState(true)

  useEffect(() => {
    if (isAuthenticated) {
      setIsHotSpotDismissed(isHelpDismissed(currentUser, helpType))
      return
    }
    setIsHotSpotDismissed(true)
  }, [currentUser, helpType, isAuthenticated])

  const handleDismissHotSpot = async () => {
    if (!showHotSpotIndicator || isHotSpotDismissed || !currentUser?.profile?.signUpCompleted) {
      return
    }
    setIsHotSpotDismissed(true)
    await dismissHelp(helpType)
    await refreshSession()
  }

  const shouldShowHotSpot = showHotSpotIndicator && !isHotSpotDismissed && currentUser?.profile?.signUpCompleted

  const { className: wrapperClassName, ...restWrapper } = wrapperProps ?? {}

  return (
    <div className={cn('relative', wrapperClassName)} onClickCapture={handleDismissHotSpot} {...restWrapper}>
      {children}
      {shouldShowHotSpot && (
        <HotSpotIndicator color={hotSpotColor} floatProps={hotSpotFloatProps ?? { className: 'right-0.5 top-0.5' }} />
      )}
    </div>
  )
}

export default DismissHelpHotSpot
